// Resolving a choice step's option images at save time (the survey page's
// per-step save): only an option whose
// assetId changed since the baseline needs its URL re-resolved — an
// unchanged option keeps its stored assetUrl verbatim, so a transient
// manifest failure for an untouched option can never wipe a working image.

import type { AssetClientContext } from "@pej/asset-client";
import type { QueryClient } from "@tanstack/react-query";
import { pickArtworkUrl } from "@/features/assets/asset-projections";
import { manifestQuery } from "@/features/assets/asset-queries";
import type { OptionFormValue, StepFormValue } from "../survey-schema";

/** Resolves one option's cached delivery URL at save time, from the shared
 *  manifest cache — generalized from the template editor's two named slots
 *  to any number of option rows. */
async function resolveOptionAssetUrl(
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
    assetId: string,
): Promise<string | null> {
    if (!ctx) return null;
    try {
        return pickArtworkUrl(await queryClient.fetchQuery(manifestQuery(ctx, assetId)));
    } catch {
        return null;
    }
}

/** The assetId persisted for an option, per the baseline (last loaded or
 *  saved) rows — "" when the option is new or never had an image. Keyed by
 *  step id + option key: rows can be added, removed, reordered or replaced
 *  wholesale (a preset swap), so index alignment can't be assumed. */
function baselineAssetId(baseline: StepFormValue[], stepId: string | null, key: string): string {
    if (stepId === null) return "";
    const step = baseline.find((candidate) => candidate.stepId === stepId);
    return step?.options.find((option) => option.key === key)?.assetId ?? "";
}

export type ResolvedSteps =
    | { ok: true; steps: StepFormValue[] }
    | { ok: false; step: number; option: number; message: string };

interface OptionResolution {
    option: OptionFormValue;
    /** The failing option's index within its step, or null. */
    failure: { option: number; message: string } | null;
}

interface StepResolution {
    step: StepFormValue;
    failure: { option: number; message: string } | null;
}

/** Re-resolves every changed choice option's image across the whole list,
 *  against `baseline`. Stops at the first failure (list order) rather than
 *  touching a form — the caller decides how to show it. Each step's options
 *  report their own outcome instead of a shared mutable flag: this compiler
 *  loses track of a `let` that a nested `.map` callback writes to. */
export async function resolveStepAssets(
    steps: StepFormValue[],
    baseline: StepFormValue[],
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
): Promise<ResolvedSteps> {
    const stepResolutions: StepResolution[] = await Promise.all(
        steps.map(async (step): Promise<StepResolution> => {
            if (step.kind !== "choice") return { step, failure: null };
            const optionResolutions: OptionResolution[] = await Promise.all(
                step.options.map(async (option, optionIdx): Promise<OptionResolution> => {
                    const changed = option.assetId !== baselineAssetId(baseline, step.stepId, option.key);
                    if (!changed) return { option, failure: null };
                    if (!option.assetId) return { option: { ...option, assetUrl: "" }, failure: null };
                    const url = await resolveOptionAssetUrl(ctx, queryClient, option.assetId);
                    if (url === null) {
                        const message = `Could not resolve the image for option '${option.key}' — try again.`;
                        return { option, failure: { option: optionIdx, message } };
                    }
                    return { option: { ...option, assetUrl: url }, failure: null };
                }),
            );
            const failure = optionResolutions.find((resolution) => resolution.failure !== null)?.failure ?? null;
            return { step: { ...step, options: optionResolutions.map((resolution) => resolution.option) }, failure };
        }),
    );

    const failedStep = stepResolutions.find((resolution) => resolution.failure !== null);
    if (failedStep?.failure) {
        return {
            ok: false,
            step: stepResolutions.indexOf(failedStep),
            option: failedStep.failure.option,
            message: failedStep.failure.message,
        };
    }
    return { ok: true, steps: stepResolutions.map((resolution) => resolution.step) };
}
